/**
 * §P.1 fase 3 · Las tallas del alumno.
 *
 * ## Aquí no se corrige: se apunta otra vez
 *
 * `student_sizes` es un histórico y el `PUT` **añade**. Eso no es un detalle de la
 * base: es lo que permite que en septiembre el club sepa que el año pasado llevó una
 * 140 y este año le toca la 150. Así que la pantalla no tiene «editar»: tiene «apuntar
 * las de hoy», y lo de antes se queda debajo con su fecha.
 *
 * Si alguien se equivoca, vuelve a apuntar la buena y la buena es la de arriba. Perder
 * la anterior para arreglar una errata sería cambiar el histórico de un niño por una
 * comodidad de un minuto.
 *
 * ## Solo se manda lo que se ha escrito
 *
 * Un campo vacío no es «sin talla»: es «hoy no la he medido». Mandarlo como cadena
 * vacía dejaría una fila que dice que el niño usa la talla «», y esa fila sería la
 * vigente a partir de entonces.
 *
 * ## Las categorías son una sugerencia, no un catálogo
 *
 * La columna es texto libre y puede llegar un «peto» apuntado antes de que existiera
 * esta pantalla. Lo que no esté en la lista se enseña igual en el histórico: si el
 * club lo apuntó, es que le sirve.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import {
  CATEGORIAS_DE_TALLA,
  etiquetaDeTalla,
  ultimasTallas,
  type TallaDeAlumno,
} from '@/club/alumnos/administracion/tipos';

export function Tallas({
  club,
  alumnoId,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  const [problema, setProblema] = useState<Problema | null>(null);
  const [guardado, setGuardado] = useState(false);

  const tallas = useQuery({
    queryKey: ['tallas', club, alumnoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .sizes.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TallaDeAlumno[];
    },
  });

  const apuntar = useMutation({
    mutationFn: async (nuevas: { category: string; size: string }[]) => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .sizes.put({ sizes: nuevas });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as TallaDeAlumno[];
    },
    onSuccess: () => {
      setBorrador({});
      setGuardado(true);
      void cache.invalidateQueries({ queryKey: ['tallas', club, alumnoId] });
    },
    onError: (e) => {
      setGuardado(false);
      setProblema(aProblema(e));
    },
  });

  if (tallas.isPending)
    return <Cargando filas={3} etiqueta="Cargando tallas" />;

  if (tallas.isError || !tallas.data) {
    return (
      <PanelError
        problema={aProblema(tallas.error)}
        reintentar={() => void tallas.refetch()}
      />
    );
  }

  const vigentes = ultimasTallas(tallas.data);

  /*
   * El histórico es todo lo que **no** es la talla vigente de su categoría. Enseñar
   * la vigente dos veces —arriba y en la lista— haría dudar de cuál manda.
   */
  const historico = tallas.data.filter((t) => vigentes.get(t.category) !== t);

  const aEnviar = Object.entries(borrador)
    .map(([category, size]) => ({ category, size: size.trim() }))
    .filter((t) => t.size !== '');

  return (
    <>
      {problema && <PanelError problema={problema} />}

      {vigentes.size === 0 ? (
        <p className="tenue">
          Todavía no hay ninguna talla apuntada. Se piden una vez y sirven cada
          septiembre.
        </p>
      ) : (
        <dl className="datos">
          {[...vigentes.values()].map((t) => (
            <div key={t.id}>
              <dt>{etiquetaDeTalla(t.category)}</dt>
              <dd>
                {t.size}
                <span className="tenue"> · {fecha(t.recorded_on)}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/*
        El formulario va sin `.formulario`: esa clase limita a 44 rem y dejaba el
        recuadro más estrecho que la lista de arriba, descuadrando los dos. Las reglas
        de `form label` e `input` alcanzan igual al elemento `form`.
      */}
      {puedeEditar && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            if (aEnviar.length > 0) apuntar.mutate(aEnviar);
          }}
        >
          <div className="rejilla">
            {CATEGORIAS_DE_TALLA.map((c) => (
              <label key={c.id}>
                {c.etiqueta}
                <input
                  maxLength={20}
                  placeholder={vigentes.get(c.id)?.size ?? c.ejemplo}
                  value={borrador[c.id] ?? ''}
                  onChange={(e) => {
                    setBorrador((b) => ({ ...b, [c.id]: e.target.value }));
                    setGuardado(false);
                  }}
                />
              </label>
            ))}
          </div>

          <div className="acciones">
            <button
              type="submit"
              className="btn-primario"
              disabled={aEnviar.length === 0 || apuntar.isPending}
            >
              {apuntar.isPending ? 'Apuntando…' : 'Apuntar las de hoy'}
            </button>

            {guardado && (
              <span className="pastilla pastilla-bien">Apuntadas</span>
            )}
          </div>

          {/*
            Se dice aquí y no en una ayuda: quien rellena esto espera estar
            corrigiendo un dato, y lo que está haciendo es añadir una medición.
          */}
          <p className="tenue">
            Lo que apuntes hoy no borra lo de antes: se guarda con su fecha, y
            por eso en septiembre se puede decir «el año pasado llevó una{' '}
            {vigentes.get('dobok')?.size ?? '140'}».
          </p>
        </form>
      )}

      {historico.length > 0 && (
        <details className="historico">
          <summary>Cómo ha ido creciendo ({historico.length})</summary>
          <ul className="lista-clara">
            {historico.map((t) => (
              <li key={t.id}>
                {etiquetaDeTalla(t.category)}: <strong>{t.size}</strong>{' '}
                <span className="tenue">
                  · {fecha(t.recorded_on)}
                  {/*
                    De dónde salió. Una talla que vino de un pedido de material la
                    escribió la familia al pedirlo, y eso explica por qué no coincide
                    con la que midió el club.
                  */}
                  {t.source === 'request' && ' · de un pedido de material'}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}
